/**
 * Auto-translation service for user content (products, CMS blocks).
 * Uses MyMemory API (works from browser, no CORS). Falls back to original on failure.
 */

const CACHE_KEY = 'ofm_translations';
const CACHE_MAX = 500;
const CHUNK_SIZE = 450; // MyMemory free tier limit per request

type LangCode = string;

const cache = new Map<string, string>();

function getCacheKey(text: string, targetLang: LangCode, sourceLang: string): string {
  return `${sourceLang}|${targetLang}:${text.slice(0, 80)}:${text.length}`;
}

function loadCache(): void {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as [string, string][];
      arr.forEach(([k, v]) => cache.set(k, v));
    }
  } catch {}
}

function saveCache(): void {
  try {
    const entries = Array.from(cache.entries()).slice(-CACHE_MAX);
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {}
}

loadCache();

function detectSourceLang(text: string): string {
  const cyrillic = /[\u0400-\u04FF]/;
  const cjk = /[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]/;
  const arabic = /[\u0600-\u06FF]/;
  if (cyrillic.test(text)) return 'ru';
  if (cjk.test(text)) return 'zh';
  if (arabic.test(text)) return 'ar';
  return 'en';
}

function buildMyMemoryUrl(text: string, src: string, target: LangCode): string {
  return `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${src}|${target}`;
}

/**
 * Splits text into chunks of max CHUNK_SIZE chars, breaking at sentence
 * boundaries (. ! ? \n) so words/sentences are never cut in the middle.
 */
function splitIntoChunks(text: string): string[] {
  if (text.length <= CHUNK_SIZE) return [text];

  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > CHUNK_SIZE) {
    let splitAt = CHUNK_SIZE;

    // Try to find a sentence boundary within the last 100 chars of the chunk
    const boundary = remaining.slice(0, CHUNK_SIZE).search(/[.!?\n][^.!?\n]*$/);
    if (boundary > 0) {
      splitAt = boundary + 1; // include the punctuation
    } else {
      // Fall back to last space to avoid cutting mid-word
      const lastSpace = remaining.lastIndexOf(' ', CHUNK_SIZE);
      if (lastSpace > 0) splitAt = lastSpace + 1;
    }

    chunks.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }

  if (remaining.length > 0) chunks.push(remaining);
  return chunks;
}

async function translateChunk(text: string, src: string, target: LangCode): Promise<string> {
  const key = getCacheKey(text, target, src);
  const cached = cache.get(key);
  if (cached) return cached;

  const url = buildMyMemoryUrl(text, src, target);
  const res = await fetch(url);
  if (!res.ok) return text;

  const data = await res.json();
  const translated: string = data?.responseData?.translatedText || text;

  if (translated && translated !== text) {
    cache.set(key, translated);
    saveCache();
  }
  return translated;
}

export async function translateText(
  text: string,
  targetLang: LangCode,
  sourceLang?: string
): Promise<string> {
  const trimmed = (text || '').trim();
  if (!trimmed) return trimmed;

  const src = sourceLang || detectSourceLang(trimmed);
  if (src === targetLang) return trimmed;

  // Check full-text cache first
  const fullKey = getCacheKey(trimmed, targetLang, src);
  const fullCached = cache.get(fullKey);
  if (fullCached) return fullCached;

  try {
    const chunks = splitIntoChunks(trimmed);

    // Translate all chunks (sequentially to avoid rate-limit hammering)
    const translatedChunks: string[] = [];
    for (const chunk of chunks) {
      const result = await translateChunk(chunk, src, targetLang);
      translatedChunks.push(result);
    }

    const result = translatedChunks.join(' ');

    // Cache the full result too
    if (result && result !== trimmed) {
      cache.set(fullKey, result);
      saveCache();
    }
    return result;
  } catch (e) {
    console.warn('[Translate]', e);
    return trimmed;
  }
}
